import { useEffect, useMemo, useState } from "react";
import type { TypedWrappedFieldProps } from "redux-form";
import _ from "underscore";

import type { ComboBoxProps } from "@braintrust/braintrust-ui-components";
import {
  ComboBox,
  Loader,
  TextField,
} from "@braintrust/braintrust-ui-components";
import type { EmployerSearchMinimalResult } from "@js/apps/employer/api";
import {
  useGetEmployerSearchItemQuery,
  useGetEmployersSearchQuery,
} from "@js/apps/employer/api";

import { useSearchPhrase } from "../../hooks/search-phrase";

type EmployerSearchComboboxFieldProps = TypedWrappedFieldProps<number | null> &
  Partial<
    Omit<ComboBoxProps<EmployerSearchMinimalResult, false>, "component">
  > & {
    label?: string;
  };

export const EmployerSearchComboboxField = ({
  input,
  meta,
  label,
  ...props
}: EmployerSearchComboboxFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<EmployerSearchMinimalResult | null>(null);
  const { onInputChange, searchPhrase } = useSearchPhrase();

  const {
    data: options,
    isFetching,
    isLoading,
  } = useGetEmployersSearchQuery({ search: searchPhrase }, { skip: !isOpen });

  const { data: initialItem, isLoading: initialTaxonomyLoading } =
    useGetEmployerSearchItemQuery(Number(meta.initial), {
      skip: meta.dirty || !meta.initial || !!value, // "dirty" is cleared with every search so we need to rely on "value" too
    });

  useEffect(() => {
    if (initialItem) {
      setValue(initialItem);
    }
  }, [initialItem]);

  useEffect(() => {
    if (!input.value) {
      setValue(null);
    }
  }, [input.value]);

  const optionsWithValueOption = useMemo(() => {
    const processedOptions = [...(options ?? []), value].filter(
      (option): option is EmployerSearchMinimalResult => Boolean(option),
    );
    const uniqueOptions = _.uniq(processedOptions, "id");

    return uniqueOptions;
  }, [options, value]);

  const isError = !!meta.error && meta.touched;

  return (
    <ComboBox<EmployerSearchMinimalResult, false>
      initialTaxonomiesLoading={initialTaxonomyLoading || isLoading}
      options={optionsWithValueOption}
      displayAllOptions
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            variant="standard"
            label={label || "Employer search"}
            error={isError}
            helperText={isError ? meta.error : undefined}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isFetching && <Loader size={20} />}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
      getOptionLabel={(option) => `${option.name} (${option.id})`}
      onInputChange={onInputChange}
      value={value}
      onChange={(_ev, valueArg) => {
        setValue(valueArg);
        input.onChange(valueArg?.id || null);
      }}
      {...props}
    />
  );
};
